import './Tariff.scss';
import { MenuItem, Select, SvgIcon } from '@mui/material';
import ButtonField from '../../Components/Button/ButtonField';
import Table from '../../Components/Table/Table';
import CustomToolbar from '../../Components/Table/components/CustomToolbar';
import TableSearch from '../../Components/Table/components/TableSearch';
import { ITariffList, TariffProps } from './ITariff';
import { FunctionComponent, useEffect, useState } from 'react';
import { ReactComponent as Add } from '../../Assets/Images/Add.svg';
import { Columns } from '../../Components/Table/ITable';
import { useAppDispatch, useAppSelector } from '../../Redux/Store/Hooks';
import { getTariff } from '../../Redux/Slicers/Tariff/TariffSlicer';
import Dialog from '../../Components/Dialog/Dialog';
import IconButtonField from '../../Components/Button/IconButtonField';
import { ReactComponent as Cancel } from '../../Assets/Images/Cancel.svg';
import TariffForm from './TariffForm';
import { FormikProps } from 'formik';
import {
  clearUpdateTariffState,
  TariffUpdateableData,
  updateTariff,
} from '../../Redux/Slicers/Tariff/UpdateTariffSlicer';
import ResponseDialog from '../../Components/ResponseDialog/ResponseDialog';
import DeleteDialog from '../../Components/DeleteDialog/DeleteDialog';
import {
  clearDeleteTariff,
  deleteTariff,
} from '../../Redux/Slicers/Tariff/DeleteTariffSlice';
import { clearAddTariffState } from '../../Redux/Slicers/Tariff/AddTariffSlicer';
import { useParams } from 'react-router-dom';
import { getParkingType } from '../../Redux/Slicers/ParkingType/ParkTypeSlicer';
import { GridActionsCellItem, GridRowParams } from '@mui/x-data-grid';
import { Delete, Edit } from '@mui/icons-material';

interface Pricing {
  parkingname: string;
  slab: {
    min_time_minutes: number;
    max_time_minutes: number;
    amount: number;
  }[];
}

interface MasterData {
  _id: string;
  pricing: Pricing[];
  isdeleted: boolean;
  default: boolean;
  created_at: string;
  updated_at: string;
}

const Tariff: FunctionComponent<TariffProps> = () => {
  const { id }: any = useParams();

  const dispatch = useAppDispatch();
  const { data } = useAppSelector((state) => state?.Tariff);

  const [open, setOpen] = useState<boolean>(false);

  const [deleteOpen, setDeleteOpen] = useState<boolean>(false);
  const [render, setRender] = useState<boolean>(false);

  const [deleteableName, setDeleteableName] = useState<string>('');
  const [deleteableId, setDeleteableId] = useState<number>(0);
  const [formik, setFormik] = useState<FormikProps<ITariffList>>();
  const { data: updateTarif, updateableDate } = useAppSelector(
    (state) => state.UpdateTariff
  );
  const { data: addTariff } = useAppSelector((state) => state.AddTariff);
  const { data: deleteTariffData } = useAppSelector(
    (state) => state.DeleteTariff
  );

  
  const { data: parkingtype, isLoading } = useAppSelector(
    (state: any) => state.ParkingType
  );
  useEffect(() => {
    setRender(!render);
  }, [updateTarif, updateableDate]);
  const isUpdateable =
    updateableDate && Object.keys(updateableDate).length !== 0;

  const createDynamicColumns = (pricing: Pricing[]) => {
    const columns: Columns = [
      {
        field: 'index',
        renderHeader: () => <b>S.No</b>,
        flex: 1,
        headerAlign: 'center',
        align: 'center',
        type: 'number',
        maxWidth: 90,
      },
      {
        field: 'tariffName',
        renderHeader: () => <b>Tariff Name</b>,
        flex: 1,
        type: 'string',
        cellClassName: 'tariffName',
      },
    ];

    // Insert dynamic pricing columns here
    pricing.forEach((price) => {
      columns.push({
        field: price.parkingname,
        renderHeader: () => <b>{price.parkingname} Parking</b>,
        flex: 1,
        type: 'string',
        maxWidth: 250,
        renderCell: (params) => <div>{params.value}</div>,
      });
    });

    // Add the status column last
    columns.push({
      field: 'default',
      renderHeader: () => <b>Status</b>,
      flex: 1,
      type: 'boolean',
      maxWidth: 190,
      renderCell: (params) => (
        <Select
          value={params.value ? 'Active' : 'Inactive'}
          onChange={(e: any) =>
            handleStatusChange(params.row.id, e.target.value)
          }>
          <MenuItem value="Active">Active</MenuItem>
          <MenuItem value="Inactive">Inactive</MenuItem>
        </Select>
      ),
      cellClassName: (params) => (params.value ? 'active' : 'inactive'),
    });

    columns.push({
      field: 'action',
      renderHeader: () => <b>Action</b>,
      flex: 1,
      type: 'actions',
      getActions: (params: GridRowParams<any>) => [
        <GridActionsCellItem
          key="Edit"
          label="Edit"
          icon={<Edit className="action_icons" />}
          onClick={() => {
            dispatch(TariffUpdateableData(params.row));
            setOpen(true);
            dispatch(getParkingType());
          }}
        />,
        <GridActionsCellItem
          key="Delete"
          label="Delete"
          icon={<Delete className="action_icons" />}
          onClick={() => {
            setDeleteOpen(true);
            setDeleteableName(params.row.name ?? '');
            setDeleteableId(params.row.id!);
          }}
        />,
      ],
      cellClassName: 'action_cell',
    });

    return columns;
  };

  const handleStatusChange = (id: string, status: string) => {
    const newStatus = status === 'Active';
    dispatch(updateTariff({ id, default: newStatus }));
    setRender(!render);
  };

  const formatParkingSlab = (slabs: Pricing['slab']) => {
    return slabs
      .map(
        (slab) =>
          `[Min: ${slab.min_time_minutes}, Max: ${slab.max_time_minutes}, Amount: ${slab.amount}]`
      )
      .join(', ');
  };

  useEffect(() => {
    if (id) {
      dispatch(getTariff(id));
    }
  }, [updateTariff, addTariff, deleteTariffData, id, updateableDate, render]);

  useEffect(() => {
    return () => {
      dispatch(clearAddTariffState());
      dispatch(clearUpdateTariffState());
      dispatch(clearDeleteTariff());
    };
  }, []);

  const data1: any = data?.response?.data;

  const masterData = Array.isArray(data1)
    ? data1.map((master, index) => {
        const pricingData: { [key: string]: string } = {};
        master.pricing.forEach((price: any) => {
          pricingData[price.parkingname] = formatParkingSlab(price.slab);
        });

        return {
          ...master,
          id: master._id,
          index: index + 1,
          ...pricingData,
        };
      })
    : [];

  const columns =
    data1 && data1.length > 0 ? createDynamicColumns(data1[0].pricing) : [];

  const masterForm = (data: FormikProps<ITariffList>) => {
    setFormik(data);
  };

  return (
    <>
      <Table
        rows={masterData ?? []}
        columns={columns}>
        <CustomToolbar>
          <h6>Tariff</h6>
          <TableSearch />
          <ButtonField
            className="add_button"
            onClick={() => {
              setOpen(true);
              dispatch(getParkingType());
            }}>
            <SvgIcon
              component={Add}
              inheritViewBox
            />
            Add New
          </ButtonField>
        </CustomToolbar>
      </Table>
      {!isLoading ? (
        <Dialog
          open={open}
          dialogClassName="master_dialog"
          dialogTitleClassName="master_dialog_title"
          Dialog_Title={
            <>
              {isUpdateable ? 'Edit Tariff' : 'Add New Tariff'}
              <IconButtonField onClick={() => setOpen(false)}>
                <SvgIcon
                  component={Cancel}
                  inheritViewBox
                />
              </IconButtonField>
            </>
          }
          Dialog_Content={<TariffForm masterForm={masterForm} />}
          dialogActionClassName="master_dialog_action"
          Dialog_Actions={
            <>
              <ButtonField
                className="cancel_button"
                onClick={() => setOpen(false)}>
                Cancel
              </ButtonField>
              <ButtonField
                className="add_button"
                onClick={() => formik?.handleSubmit()}>
                {isUpdateable ? 'Update' : 'Save'}
              </ButtonField>
            </>
          }
        />
      ) : (
        'loading...'
      )}

      <ResponseDialog
        response={addTariff}
        parentDialog={setOpen}
      />
      <ResponseDialog
        response={updateTarif}
        parentDialog={setOpen}
      />

      <DeleteDialog
        deleteableName={deleteableName}
        open={deleteOpen}
        setOpen={setDeleteOpen}
        deleteFunction={() => dispatch(deleteTariff(deleteableId))}
        response={deleteTariffData}
      />
    </>
  );
};

export default Tariff;
